// `extension-alert-create-client-contact-waiting-modal.tpl.html` — the spinner that follows a sent
// "Create new contact" / "Update existing contact", shown by `onCreateInAtsWaitingModal` with
// `windowClass: "dialog-unified shortlist-dialogue create-client-contact-waiting-modal"`.
//
// ⚠️ IT WATCHES THE RESULT, IT DOES NOT FETCH IT. The poll belongs to the screen's
// `ClientContactAsyncContainer`, and the snippet's completion callback writes
// `clientContactAddToAtsResult`; this only checks once a second whether that has arrived or ten
// seconds have passed, and stops spinning on whichever comes first.
//
// ⚠️ AN UPDATE NEVER SHOWS A RESULT. `onClientContactUpdatedPendingOperationCb` clears the pending
// flag and does not write `clientContactAddToAtsResult` — so an update's modal spins for ten seconds
// and then says "This is taking longer than expected" however quickly the update completed (or
// shows a PREVIOUS create's result, which the controller never resets). Upstream's, kept.
import { useEffect, useRef, useState, type ReactElement } from 'react'
import { AddAtsClientContactContext, type ClientContactAddAtsCompletedOperationResult } from '@woo-ext/api'
import { UnifiedModal, type ModalCloseAction } from '../modal/UnifiedModal.tsx'
import { CREATE_IN_ATS_WAIT_SECONDS } from './createClientContact.ts'

export interface CreateClientContactWaitingModalProps {
  context: AddAtsClientContactContext
  /** `::newContactName` — one-time bound: the name as it was sent. */
  newContactName: string | null | undefined
  /** `ctrl.clientContactAddToAtsResult`, live. */
  result: ClientContactAddAtsCompletedOperationResult | null | undefined
  /** `::goToATSCopy`. */
  goToAtsCopy: string
  /** `scope.notifyMe` — closes; the caller decides whether the snippet closes with it. */
  onNotifyMe: () => void
  /** `scope.openInAts` — then closes. */
  onOpenInAts: () => void
  onClose: () => void
}

/** `modal.closing` — backdrop and Escape are refused; the ✕ still closes. */
function mayClose(action: ModalCloseAction): boolean {
  return action !== 'backdrop click' && action !== 'escape key press'
}

export function CreateClientContactWaitingModal({
  context,
  newContactName,
  result,
  goToAtsCopy,
  onNotifyMe,
  onOpenInAts,
  onClose,
}: CreateClientContactWaitingModalProps): ReactElement {
  const [inProgress, setInProgress] = useState(true)
  const [name] = useState(newContactName)
  const resultRef = useRef(result)
  useEffect(() => {
    resultRef.current = result
  }, [result])

  // `$interval(…, 1000)` from the moment the modal opens; cancelled on close by unmounting.
  useEffect(() => {
    const startTime = Date.now()
    const handle = setInterval(() => {
      const timePassed = (Date.now() - startTime) / 1000
      if (!!resultRef.current || timePassed >= CREATE_IN_ATS_WAIT_SECONDS) {
        setInProgress(false)
        clearInterval(handle)
      }
    }, 1000)
    return () => {
      clearInterval(handle)
    }
  }, [])

  const create = context === AddAtsClientContactContext.CREATE
  const update = context === AddAtsClientContactContext.UPDATE

  return (
    <UnifiedModal
      open
      windowClass="dialog-unified shortlist-dialogue create-client-contact-waiting-modal"
      onClosing={mayClose}
      onClose={onClose}
    >
      <div className="extension-client-contact-add-to-ats-overlay">
        {/* `ng-show` both ways; rendered conditionally, as elsewhere here (`.ng-hide` is display: none). */}
        {!inProgress ? null : (
          <div>
            <div className="client-contact-add-to-ats-waiting-spinner">
              <i className="fa fa-2x fa-spinner fa-spin" />
            </div>
            {!create ? null : <div className="create-client-contact-text client-contact-text-center mT15">Creating a contact...</div>}
            {!update ? null : <div className="create-client-contact-text client-contact-text-center mT15">Updating contact...</div>}
          </div>
        )}
        {inProgress ? null : (
          <div>
            {result ? null : (
              <div>
                <div className="create-client-contact-text add-to-ats-response-delay">
                  This is taking longer than expected.
                  <br />
                  <br />
                  {!create ? null : <span>Click here if you wish to be notified once the contact is created.</span>}
                  {!update ? null : <span>Click here if you wish to be notified once the contact is updated.</span>}
                </div>
                <div className="client-contact-add-to-ats-waiting-button mT15">
                  <button type="button" className="ok-button" onClick={onNotifyMe}>
                    Notify me
                  </button>
                </div>
              </div>
            )}
            {!result ? null : (
              <div>
                {!result.successful ? null : (
                  <div>
                    <div className="create-client-contact-text client-contact-text-center">
                      {`Contact details have been successfully added and updated for ${name ?? ''}`}
                    </div>
                    <div className="client-contact-add-to-ats-waiting-button mT15">
                      <button type="button" className="go-to-ats-button" onClick={onOpenInAts}>
                        {goToAtsCopy}
                      </button>
                    </div>
                  </div>
                )}
                {result.successful ? null : (
                  <div>
                    <div className="create-client-contact-text client-contact-text-center">The action failed. Please try again.</div>
                    <div className="client-contact-add-to-ats-waiting-button mT15">
                      <button type="button" className="go-to-ats-button" onClick={onClose}>
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </UnifiedModal>
  )
}
